/**
 * Tracks the page a user navigated *from*, so the listing details visit can
 * report its origin as `from_page`.
 *
 * Next.js client-side navigations never touch `document.referrer`, so the origin
 * is recorded when a link is clicked (`AppShell`) into sessionStorage.
 * The record also stores the destination path, which lets the reader confirm the
 * record actually belongs to the page asking for it instead of being a leftover
 * from an earlier navigation (e.g. after a full page reload).
 *
 * `from_page` is a short slug, the same kind the app sends (`home`, `booking`,
 * `search`), so web and app visits group together in the analytics dashboard.
 */

const NAV_ORIGIN_KEY = "travela_nav_origin";

interface INavOriginRecord {
  /** Pathname the navigation started from. */
  from: string;
  /** Query string of the origin page, without the leading `?`. */
  fromQuery: string;
  /** Pathname the navigation was headed to. */
  to: string;
  /** Set when the link sat inside a `data-from-page` section, e.g. a homepage rail. */
  fromPage?: string;
}

/** The section a link sits in names the click's `from_page` (`homepage_{tab}_{rail_id}`). */
export const FROM_PAGE_ATTRIBUTE = "data-from-page";

export function linkFromPage(link: Element): string | undefined {
  return (
    link
      .closest(`[${FROM_PAGE_ATTRIBUTE}]`)
      ?.getAttribute(FROM_PAGE_ATTRIBUTE) || undefined
  );
}

/** Pathname portion of an absolute or relative url. Empty string when unparsable. */
const toPathname = (url: string): string => {
  if (typeof window === "undefined") return "";
  try {
    const parsed = url.startsWith("http")
      ? new URL(url)
      : new URL(url, window.location.origin);
    return parsed.pathname;
  } catch (_e) {
    return "";
  }
};

/**
 * Whether the app has moved past the page the document loaded with: a link followed
 * or a back / forward. Until then, that page is the one the browser loaded (or reloaded).
 */
let navigatedSinceLoad = false;

/** Call on a back / forward: the page on screen is no longer the loaded one. */
export function markClientNavigation(): void {
  navigatedSinceLoad = true;
}

/**
 * Whether the page on screen is the one the browser just reloaded (F5, pull to refresh),
 * not a new visit: a reload counts its view again otherwise.
 */
export function isReloadOfLoadedPage(): boolean {
  if (typeof window === "undefined" || navigatedSinceLoad) return false;
  try {
    const [entry] = performance.getEntriesByType(
      "navigation",
    ) as PerformanceNavigationTiming[];
    return entry?.type === "reload";
  } catch (_e) {
    return false;
  }
}

/**
 * Call when a link is followed: stores the current page as the origin of the
 * navigation towards `nextUrl`.
 */
export function recordNavigationOrigin(
  nextUrl: string,
  fromPage?: string,
): void {
  if (typeof window === "undefined") return;
  const to = toPathname(nextUrl);
  if (!to || to === window.location.pathname) return;
  navigatedSinceLoad = true;
  try {
    const record: INavOriginRecord = {
      from: window.location.pathname,
      fromQuery: window.location.search.replace(/^\?/, ""),
      to,
      ...(fromPage ? { fromPage } : {}),
    };
    sessionStorage.setItem(NAV_ORIGIN_KEY, JSON.stringify(record));
  } catch (_e) {
    // sessionStorage can throw in private mode — tracking is best effort.
  }
}

/** Fired when a navigation to another page starts; `detail` is its URL. */
/** Controls a card puts inside its link: they do their own thing, not the link's. */
const CONTROL_IN_LINK =
  'button, [role="button"], input, select, textarea, label, summary';

/**
 * Whether a click on `target` follows `link`. A click on a control inside the link (a
 * card's save heart, its photo arrows and dots) stops the link itself, so it is no
 * navigation, even though a capture listener sees it before the control's handler runs.
 */
export function clickFollowsLink(
  target: Element,
  link: HTMLAnchorElement,
): boolean {
  const control = target.closest(CONTROL_IN_LINK);
  return !control || !link.contains(control);
}

export const NAVIGATION_START_EVENT = "travela:navigationstart";

/**
 * A navigation to another page is starting (a link followed, a `push`): records where it
 * started from and tells the shell, which shows the top loading bar until it arrives.
 */
export function announceNavigation(nextUrl: string, fromPage?: string): void {
  if (typeof window === "undefined") return;
  const to = toPathname(nextUrl);
  if (!to || to === window.location.pathname) return;
  recordNavigationOrigin(nextUrl, fromPage);
  window.dispatchEvent(
    new CustomEvent<string>(NAVIGATION_START_EVENT, { detail: to }),
  );
}

/** First path segment → `from_page` slug. Unlisted pages use the segment itself. */
const PAGE_SLUGS: Record<string, string> = {
  "": "home",
  s: "search",
  listing: "listing",
  hotel: "hotel",
  hotels: "hotels",
  rooms: "rooms",
  room: "rooms",
  booking: "booking",
  chat: "conversation",
  profile: "profile",
  notifications: "notifications",
  blogs: "blog",
  "host-details": "host",
  "popular-locations": "popular_locations",
  "explore-more": "explore_more",
  "reliable-stays-across-bangladesh": "reliable_stays",
};

/** `from_page` slug for a same-site pathname. */
function pageSlug(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  let segment = (segments[0] || "").toLowerCase();
  // A campaign page is `/{title}-{id}` (`/Rooms-2`); report the campaign, not its id.
  if (segments.length === 1) segment = segment.replace(/-\d+$/, "");
  const known = PAGE_SLUGS[segment];
  if (known) return known;
  return segment.replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "home";
}

/** Referring sites grouped under one name, matched on the hostname. */
const EXTERNAL_SOURCES: Array<[RegExp, string]> = [
  [/(^|\.)google\./, "google"],
  [/(^|\.)(facebook\.com|fb\.com|fb\.me)$/, "facebook"],
  [/(^|\.)instagram\.com$/, "instagram"],
  [/(^|\.)(youtube\.com|youtu\.be)$/, "youtube"],
  [/(^|\.)bing\.com$/, "bing"],
];

/** `from_page` for an off-site referrer: a known source name, else its host. */
function externalSourceSlug(hostname: string): string {
  const host = hostname.toLowerCase().replace(/^www\./, "");
  for (const [pattern, name] of EXTERNAL_SOURCES) {
    if (pattern.test(host)) return name;
  }
  return host;
}

/** Where a full page load came from, read off `document.referrer`. */
function referrerOrigin(): { fromPage: string; fromQuery: string } | null {
  if (typeof window === "undefined") return null;
  const referrer = document.referrer;
  if (!referrer) return null;
  try {
    const parsed = new URL(referrer);
    if (parsed.origin === window.location.origin) {
      return {
        fromPage: pageSlug(parsed.pathname),
        fromQuery: parsed.search.replace(/^\?/, ""),
      };
    }
    return { fromPage: externalSourceSlug(parsed.hostname), fromQuery: "" };
  } catch (_e) {
    return null;
  }
}

/**
 * Origin of the visit to `currentPath`: the recorded in-app navigation, else the
 * referrer of a full page load (same site or e.g. `google`), else `direct`.
 */
export function getNavigationOrigin(currentPath: string): {
  fromPage: string;
  fromQuery: string;
} {
  const fallback = referrerOrigin() ?? { fromPage: "direct", fromQuery: "" };
  if (typeof window === "undefined") return fallback;

  try {
    const raw = sessionStorage.getItem(NAV_ORIGIN_KEY);
    if (!raw) return fallback;
    const record = JSON.parse(raw) as INavOriginRecord;
    // Only trust the record when it was written for this exact destination.
    if (!record?.from || record.to !== currentPath) return fallback;
    return {
      fromPage: record.fromPage || pageSlug(record.from),
      fromQuery: record.fromQuery || "",
    };
  } catch (_e) {
    return fallback;
  }
}
